import React, { useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaFileInvoice,
  FaPrint,
  FaReceipt,
  FaUser,
} from "react-icons/fa";
import { printReceipt } from "../utils/printReceipt";
import { customerMobileOf, formatAmount } from "../utils/commonUtils";
import InvoiceModal from "../components/InvoiceModal";
import Message from "../components/Message";
import useCustomersCache from "../useCustomersCache";
import { useGetOrdersQuery } from "../slices/OrdersSlice";

const headStyle = {
  backgroundColor: "#f9fafb",
  color: "#374151",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  borderBottom: "2px solid #eef0f3",
  padding: "0.6rem 0.75rem",
  whiteSpace: "nowrap",
};

const cellStyle = {
  padding: "0.55rem 0.75rem",
  verticalAlign: "middle",
  fontSize: "0.88rem",
};

const OrderDetailsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderResponse = location.state?.orderResponse;
  const orderDetails = Array.isArray(orderResponse?.orderDetails)
    ? orderResponse.orderDetails
    : [];

  const { userInfo } = useSelector((state) => state.auth);

  // Keeps userInfo.customersList populated for the customer card and the
  // invoice modal's "TO" section
  const { customersList } = useCustomersCache();

  const [showInvoice, setShowInvoice] = useState(false);

  const mobileNumber = String(orderResponse?.mobileNumber || "").trim();

  // The selected outlet's customer record for this mobile number
  const customer = customersList?.find(
    (c) => customerMobileOf(c) === mobileNumber
  );

  // Total outstanding for this customer = sum of dues across all their
  // orders that still have a pending amount
  const { data: dueOrders } = useGetOrdersQuery(
    {
      query: `dueAmount[gt]=0&mobileNumber=${mobileNumber}`,
      token: userInfo?.token,
    },
    { skip: !mobileNumber }
  );
  const customerTotalDue = (Array.isArray(dueOrders) ? dueOrders : []).reduce(
    (sum, order) => sum + Number(order?.dueAmount || 0),
    0
  );

  const totalItems = orderDetails.reduce(
    (total, it) => total + it.itemQuantity,
    0
  );

  const orderDate = orderResponse?.createdAt
    ? new Date(orderResponse.createdAt).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not available";

  const printBill = async (order) => {
    await printReceipt(order, userInfo, "manual");
  };

  if (!orderResponse) {
    return (
      <div className="od-page">
        <Button
          variant="outline-primary"
          className="od-back-btn"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft /> Back
        </Button>
        <div style={{ marginTop: "1rem" }}>
          <Message variant="danger">
            No order selected. Please open this page from an order.
          </Message>
        </div>
      </div>
    );
  }

  return (
    <div className="od-page">
      {/* Top bar : back + actions */}
      <div className="od-topbar">
        <Button
          variant="outline-primary"
          className="od-back-btn"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft /> Back
        </Button>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button
            variant="outline-primary"
            style={{ borderRadius: "0.5rem" }}
            onClick={() => setShowInvoice(true)}
          >
            <FaFileInvoice style={{ marginBottom: "2px" }} /> Invoice
          </Button>
          <Button
            variant="primary"
            style={{ borderRadius: "0.5rem" }}
            onClick={() => printBill(orderResponse)}
          >
            <FaPrint style={{ marginBottom: "2px" }} /> Print
          </Button>
        </div>
      </div>

      {/* Hero : order id + badges */}
      <div className="od-hero">
        <h2 className="od-hero-name">Order {orderResponse?.orderId}</h2>
        <div className="od-hero-sub">
          {orderDate}
          {orderResponse?.invoiceNumber
            ? ` · Invoice: ${orderResponse.invoiceNumber}`
            : ""}
        </div>
        <div className="od-badges">
          {orderResponse?.orderStatus && (
            <span
              className={`od-badge ${
                orderResponse.orderStatus === "Delivered"
                  ? "od-badge-open"
                  : "od-badge-neutral"
              }`}
            >
              {orderResponse.orderStatus}
            </span>
          )}
          {orderResponse?.orderType && (
            <span className="od-badge od-badge-neutral">
              {orderResponse.orderType}
            </span>
          )}
          {orderResponse?.paymentMode && (
            <span className="od-badge od-badge-neutral">
              {orderResponse.paymentMode}
            </span>
          )}
          {Number(orderResponse?.dueAmount) > 0 && (
            <span className="od-badge od-badge-closed">
              Due: ₹ {formatAmount(orderResponse.dueAmount)}
            </span>
          )}
          {!userInfo?.deliveryTo?.includes("undefined") &&
            userInfo?.deliveryTo && (
              <span className="od-badge od-badge-neutral">
                Seat: {userInfo.deliveryTo}
              </span>
            )}
        </div>
      </div>

      <div className="od-grid" style={{ marginBottom: "1rem" }}>
        {/* Order summary */}
        <div className="od-card">
          <div className="od-card-header">
            <FaReceipt /> Order Summary
          </div>
          <div className="od-card-body">
            <div className="od-row">
              <span className="od-label">Items</span>
              <span className="od-value">{totalItems}</span>
            </div>
            {orderResponse?.deliveryOtp && (
              <div className="od-row">
                <span className="od-label">Delivery OTP</span>
                <span className="od-value">{orderResponse.deliveryOtp}</span>
              </div>
            )}
            {orderResponse?.orderType?.toLowerCase() === "online" &&
              typeof orderResponse?.platformFee === "number" && (
                <div className="od-row">
                  <span className="od-label">Platform Fee</span>
                  <span className="od-value">
                    ₹ {formatAmount(orderResponse.platformFee)}
                  </span>
                </div>
              )}
            {Number(orderResponse?.dueAmount) > 0 && (
              <div className="od-row">
                <span className="od-label">Pending on this order</span>
                <span className="od-value" style={{ color: "#c0392b" }}>
                  ₹ {formatAmount(orderResponse.dueAmount)}
                </span>
              </div>
            )}
            <div className="od-row">
              <span className="od-label">Total Amount</span>
              <span
                className="od-value"
                style={{ color: "#1e8e4e", fontSize: "1.05rem" }}
              >
                ₹ {formatAmount(orderResponse?.grossTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Customer details - only when the order carries a mobile number */}
        {mobileNumber && (
          <div className="od-card">
            <div className="od-card-header">
              <FaUser /> Customer Details
            </div>
            <div className="od-card-body">
              {customer?.customerName && (
                <div className="od-row">
                  <span className="od-label">Name</span>
                  <span className="od-value">{customer.customerName}</span>
                </div>
              )}
              <div className="od-row">
                <span className="od-label">Mobile</span>
                <span className="od-value">{mobileNumber}</span>
              </div>
              {customer?.customerGst && (
                <div className="od-row">
                  <span className="od-label">GST Number</span>
                  <span className="od-value">{customer.customerGst}</span>
                </div>
              )}
              {customer?.postPaidCustomer && (
                <div className="od-row">
                  <span className="od-label">Postpaid Customer</span>
                  <span className="od-value">
                    <span className="od-pill od-pill-yes">Yes</span>
                  </span>
                </div>
              )}
              <div className="od-row">
                <span className="od-label">Total Due (all orders)</span>
                <span
                  className="od-value"
                  style={{
                    color: customerTotalDue > 0 ? "#c0392b" : "#1e8e4e",
                  }}
                >
                  ₹ {formatAmount(customerTotalDue)}
                </span>
              </div>
              {customer?.customerAddress && (
                <>
                  <div className="od-row">
                    <span className="od-label">Address</span>
                  </div>
                  <div className="od-block-value">
                    {customer.customerAddress}
                    {customer?.customerState
                      ? `\n${customer.customerState}`
                      : ""}
                  </div>
                </>
              )}
              {!customer && (
                <div className="od-row">
                  <span className="od-label">
                    Customer not found in customers list
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Items table */}
      <div className="od-card" style={{ marginBottom: "1rem" }}>
        <div className="od-card-header">
          <FaReceipt /> Ordered Items
        </div>
        <div style={{ overflowX: "auto" }}>
          <Table hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th style={headStyle}>Item</th>
                <th style={{ ...headStyle, textAlign: "right" }}>
                  Price/Item
                </th>
                <th style={{ ...headStyle, textAlign: "right" }}>Tax</th>
                <th style={{ ...headStyle, textAlign: "center" }}>Qty</th>
                <th style={{ ...headStyle, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {orderDetails.map((it, index) => (
                <tr key={it.itemId || index}>
                  <td style={cellStyle}>
                    {it.itemName}
                    {it.sizeName !== "Single Size" && (
                      <small style={{ color: "#1e7e34" }}> {it.sizeName}</small>
                    )}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>
                    ₹ {formatAmount(it.sellingPriceAfterOffers)}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>
                    ₹ {formatAmount(it.gstAmount)}{" "}
                    <small className="text-muted">({it.gstTax}%)</small>
                  </td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {it.itemQuantity}
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: "right",
                      fontWeight: "600",
                    }}
                  >
                    ₹{" "}
                    {formatAmount(
                      it.itemQuantity *
                        (it.sellingPriceAfterOffers + it.gstAmount)
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...cellStyle, fontWeight: "700" }}>Total</td>
                <td style={cellStyle}></td>
                <td style={cellStyle}></td>
                <td
                  style={{
                    ...cellStyle,
                    textAlign: "center",
                    fontWeight: "700",
                  }}
                >
                  {totalItems}
                </td>
                <td
                  style={{
                    ...cellStyle,
                    textAlign: "right",
                    fontWeight: "700",
                    color: "#1e8e4e",
                  }}
                >
                  ₹ {formatAmount(orderResponse?.grossTotal)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>

      {showInvoice && (
        <InvoiceModal
          show={showInvoice}
          onHide={() => setShowInvoice(false)}
          order={orderResponse}
        />
      )}
    </div>
  );
};

export default OrderDetailsScreen;
